import {
  useWSModalCustom,
  useWSSnackbar,
  WSForm,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSelect
} from "@wingspanhq/fe-component-library";
import { WorkDefinition, WorkItem } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import * as Yup from "yup";
import { queriesWorkItems } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { FormPartialAttributes } from "../../components/FormPartialAttributes";
import { AcceptedValuesResources } from "../../components/FormPartialAttributes/helpers";
import { getAttributeValidators } from "../../components/FormPartialAttributes/validation";
import { PublicCommentField } from "../../components/PublicCommentField";
import { stripNullAttributes } from "../../utils/stripNullAttributes";

type Props = {
  workItem: WorkItem;
  workDefinition: WorkDefinition;
  resources: AcceptedValuesResources;
  clientName?: string;
} & WSModalCustomProps<any>;

export const ModalEditItem: React.FC<Props> = ({
  workItem,
  workDefinition,
  resources,
  clientName,
  ...modalProps
}) => {
  const payerCommentExists = !!workItem.payerOwnedData?.comment;

  const validationSchema = useMemo(() => {
    const attributeValidators = getAttributeValidators(
      workDefinition.attributeDefinitions ?? []
    );

    return Yup.object().shape({
      ...attributeValidators,
      ...(payerCommentExists
        ? {
            comment: Yup.string().required("A comment is required when editing")
          }
        : {})
    });
  }, [workDefinition, payerCommentExists]);

  const [mutate, meta] = queriesWorkItems.updateWorkItem.useMutation();

  const { openSnackbar } = useWSSnackbar();

  const commentDescription = clientName
    ? `Comment is visible to both you and the client. Please provide specific details that will inform ${clientName} about the edits.`
    : "Comment is visible to both you and the client. Please provide specific details about the edits.";

  return (
    <WSForm
      defaultValues={{
        ...(workItem.attributes ?? {}),
        ...(payerCommentExists ? { comment: "" } : {})
      }}
      validationSchema={validationSchema}
      onSubmit={({ comment, ...attributes }) => {
        mutate(
          {
            workItemId: workItem.id!,
            workItemUpdate: {
              attributes: stripNullAttributes(attributes),
              ...(payerCommentExists && comment
                ? { payeeOwnedData: { comment } }
                : {})
            }
          },
          {
            onSuccess: () => {
              modalProps.onClose(undefined);

              openSnackbar({
                icon: {
                  name: "check"
                },
                message: "Work item updated"
              });
            }
          }
        );
      }}
    >
      <WSModal
        title="Edit item"
        actions={{
          alignment: "fill",
          buttons: [
            {
              kind: "Secondary",
              label: "Cancel",
              type: "button",
              onClick: () => {
                modalProps.onClose(undefined);
              }
            },
            {
              kind: "Primary",
              label: "Save changes",
              loading: meta.isLoading
            }
          ]
        }}
        {...modalProps}
      >
        <WSList gap="L">
          <WSSelect
            disabled
            value={workDefinition.id}
            onChange={() => {
              // Do nothing, fields should be permanently disabled
            }}
            label="Work item"
            required
            options={[
              {
                value: workDefinition.id || "",
                label: workDefinition.name || ""
              }
            ]}
          />

          <FormPartialAttributes
            attributeDefinitions={workDefinition.attributeDefinitions ?? []}
            resources={resources}
          />

          {payerCommentExists && (
            <PublicCommentField
              label="Comment *"
              description={commentDescription}
              placeholder="Provide specific details about the edits"
            />
          )}

          <ErrorAlert error={meta.error} context="WorkItem" />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalEditItem = () => useWSModalCustom(ModalEditItem);
